import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  hasLevels,
  LOCATION_TYPE_LABEL_HU,
  parseBarcode,
  type LocationView,
} from '@persped/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { newIdempotencyKey } from '../../api/client';
import {
  useCreateFolder,
  useLocations,
  useReprintFolderLabel,
  useUploadFolderImage,
} from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';
import { prepareImage, type PreparedImage } from './image';

/**
 * Mappa regisztrálása a weben (US-FOLDER-6).
 *
 * A spec 5.2 §-a szerint a web „fotók FELTÖLTÉSE és megtekintése" — nem
 * rögzítés. A gerincfotót tehát egy már meglévő fájlból választjuk; a
 * kamerás rögzítés a Zebra app dolga, ahol a dolgozó a mappát a kezében
 * tartja.
 *
 * **A sorrend és az idempotencia-kulcs itt nem részletkérdés.** A
 * regisztráció két hívás (mappa + fotó), és a kettő között elszakadhat a
 * hálózat. Ezért:
 *  - mindkét hívás kulcsát EGYSZER képezzük, a művelet indításakor;
 *  - újrapróbáláskor UGYANAZT küldjük, tehát a szerver a tárolt választ
 *    adja vissza, nem hoz létre második mappát;
 *  - ha a mappa létrejött, de a fotó elakadt, csak a fotót ismételjük.
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const createFolder = useCreateFolder();
  const uploadImage = useUploadFolderImage();
  const reprint = useReprintFolderLabel();
  const locations = useLocations({ status: 'ACTIVE' });

  const [barcode, setBarcode] = useState('');
  const [text, setText] = useState('');
  const [target, setTarget] = useState<LocationView | null>(null);
  const [level, setLevel] = useState('');
  const [image, setImage] = useState<PreparedImage | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [keys, setKeys] = useState<{ folder: string; image: string } | null>(null);

  const parsed = parseBarcode(barcode);
  const barcodeOk = parsed.ok && parsed.kind === 'FOLDER';
  // Szintes célhelynél kötelező a szint — a polc is szintes (D fázis), nem
  // csak a raklap.
  const needsLevel = target !== null && hasLevels(target.locationType);
  const levelMissing = needsLevel && level === '';

  // A mappa akkor is létrejöhetett, ha a fotó elakadt — ilyenkor csak a
  // fotót kell ismételni, nem az egészet.
  const folderCreated = createFolder.isSuccess;
  const done = folderCreated && uploadImage.isSuccess;

  async function onPickFile(file: File | undefined) {
    if (!file) return;
    setImageError(null);
    setPreparing(true);
    try {
      setImage(await prepareImage(file));
    } catch (e) {
      setImage(null);
      setImageError(e instanceof Error ? e.message : 'A kép feldolgozása nem sikerült.');
    } finally {
      setPreparing(false);
    }
  }

  async function submit() {
    if (!barcodeOk || !image) return;

    const k = keys ?? { folder: newIdempotencyKey(), image: newIdempotencyKey() };
    setKeys(k);

    // A hibát a mutációk állapota hordozza, és a `ProblemAlert` jeleníti
    // meg — itt csak azért kapjuk el, hogy ne maradjon kezeletlen
    // ígéret-elutasítás. A `mutateAsync` kell a `mutate` helyett, mert a
    // két hívásnak SORRENDBEN kell futnia: fotót csak létező mappára lehet
    // feltölteni.
    try {
      if (!folderCreated) {
        await createFolder.mutateAsync({
          barcode,
          currentText: text,
          targetLocationBarcode: target?.barcode,
          targetPalletLevel: level ? Number(level) : undefined,
          idempotencyKey: k.folder,
        });
      }
      await uploadImage.mutateAsync({
        barcode,
        blob: image.blob,
        checksum: image.checksum,
        idempotencyKey: k.image,
      });
    } catch {
      // szándékosan üres — ld. fent
    }
  }

  const busy = createFolder.isPending || uploadImage.isPending || preparing;

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h2">
        Mappa regisztrálása
      </Typography>

      {done ? (
        <Stack spacing={2}>
          <Alert
            severity="success"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void navigate(`/mappak/${barcode}`)}
              >
                Adatlap
              </Button>
            }
          >
            <AlertTitle>{barcode} elmentve</AlertTitle>
            A gerincfotó feltöltve, a felismerés elindult.
          </Alert>

          {/*
            Címkenyomtatás azonnal (08-13 CR 7.3). Az újranyomtatási végpontot
            hívja, mert a vonalkód a regisztrációval MÁR ki van osztva — a
            kiosztás és a nyomtatás két külön tény (3. alapelv), és itt a
            második hiányzik.
          */}
          {reprint.isSuccess ? (
            <Alert severity="success">A címke nyomtatásra küldve.</Alert>
          ) : (
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Button
                variant="outlined"
                disabled={reprint.isPending || !uploadImage.data}
                onClick={() => {
                  const f = uploadImage.data;
                  if (!f) return;
                  reprint.mutate({
                    barcode,
                    // A frissen feltöltött gerincfotó ÉS a friss verzió — a
                    // szerver ezzel kényszeríti ki, hogy a nyomtatás egy
                    // konkrét, megerősített mappára szóljon. Itt ez ingyen
                    // van: a fotót az imént töltöttük fel.
                    confirmedImageId: f.images[0]?.id ?? '',
                    rowVersion: f.rowVersion,
                    reason: 'Nyomtatás közvetlenül a regisztráció után',
                  });
                }}
              >
                Címke nyomtatása
              </Button>
              <Typography variant="body2" color="text.secondary">
                Ha a mappára még nincs felragasztva a címke.
              </Typography>
            </Stack>
          )}
          {reprint.isError && <ProblemAlert error={reprint.error} />}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Mappa vonalkód"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value.toUpperCase())}
              error={barcode.length > 0 && !barcodeOk}
              helperText="Várt formátum: MAP-00001. Csatlakoztatott vonalkódolvasó is használható."
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              disabled={folderCreated}
              autoFocus
              fullWidth
            />

            <TextField
              label="Gerincszöveg"
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={3}
              disabled={folderCreated}
              fullWidth
            />

            <Autocomplete
              options={locations.data ?? []}
              value={target}
              onChange={(_, next) => {
                setTarget(next);
                setLevel('');
              }}
              disabled={folderCreated}
              getOptionLabel={(l) => `${l.barcode} — ${l.name}`}
              groupBy={(l) => LOCATION_TYPE_LABEL_HU[l.locationType]}
              isOptionEqualToValue={(a, b) => a.barcode === b.barcode}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Azonnali elhelyezés (opcionális)"
                  helperText="Üresen hagyva a mappa az alapértelmezett Előkészítőbe kerül."
                />
              )}
            />

            {needsLevel && target.levels && (
              <TextField
                select
                label="Szint"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                disabled={folderCreated}
                helperText="Raklapra és polcra helyezéskor kötelező."
                fullWidth
              >
                {target.levels.map((l) => (
                  <MenuItem key={l.levelNumber} value={String(l.levelNumber)}>
                    {l.levelNumber}. szint — {l.folderCount} mappa
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Stack spacing={1}>
              <Button component="label" variant="outlined" disabled={busy}>
                {image ? 'Másik gerincfotó választása' : 'Gerincfotó kiválasztása'}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  hidden
                  onChange={(e) => void onPickFile(e.target.files?.[0])}
                />
              </Button>
              {preparing && <LinearProgress />}
              {image && (
                <Typography variant="caption" color="text.secondary">
                  {image.width}×{image.height} px, {Math.round(image.blob.size / 1024)} kB — a kép
                  a feltöltés előtt átméreteződött (ugyanúgy, mint a Zebra appban).
                </Typography>
              )}
              {imageError && <Alert severity="error">{imageError}</Alert>}
            </Stack>

            {folderCreated && !uploadImage.isSuccess && (
              <Alert severity="warning">
                <AlertTitle>A mappa létrejött, a fotó még nem</AlertTitle>
                Ne kezdd elölről — a „Mentés" most már csak a fotót küldi újra.
              </Alert>
            )}

            {createFolder.isError && <ProblemAlert error={createFolder.error} />}
            {uploadImage.isError && <ProblemAlert error={uploadImage.error} />}

            <Stack direction="row">
              <Button
                variant="contained"
                disabled={!barcodeOk || !image || levelMissing || busy}
                onClick={() => void submit()}
              >
                {folderCreated ? 'Fotó újraküldése' : 'Mentés'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
